import { NEUTRAL_STYLE, type MemberView } from '../members';

/**
 * Avatar, avatar stack and filter chips (design: Component · MemberChip). Colour never
 * carries meaning alone — an initial or a name always rides along.
 */

const SIZES = {
  sm: 'h-5 w-5 text-[10px]',
  md: 'h-6 w-6 text-[11px]',
  lg: 'h-9 w-9 text-[13px]',
} as const;

export function MemberAvatar({
  member,
  size = 'md',
  ring = false,
}: {
  member: MemberView | undefined;
  size?: keyof typeof SIZES;
  ring?: boolean;
}): JSX.Element {
  const style = member?.style ?? NEUTRAL_STYLE;
  return (
    <span
      title={member?.name ?? 'Unknown member'}
      style={style.fill}
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-bold ${SIZES[size]} ${ring ? 'ring-2 ring-surface' : ''}`}
    >
      {member?.avatarEmoji ?? member?.initial ?? '?'}
    </span>
  );
}

export function MemberStack({
  ownerIds,
  byId,
  size = 'md',
}: {
  ownerIds: readonly string[];
  byId: ReadonlyMap<string, MemberView>;
  size?: keyof typeof SIZES;
}): JSX.Element | null {
  if (ownerIds.length === 0) return null;
  return (
    <span className="flex shrink-0 items-center">
      {ownerIds.slice(0, 3).map((id, index) => (
        <span key={id} className={index === 0 ? '' : '-ml-2'}>
          <MemberAvatar member={byId.get(id)} size={size} ring />
        </span>
      ))}
      {ownerIds.length > 3 && (
        <span className="ml-1 font-mono text-[10px] text-fg-3">+{ownerIds.length - 3}</span>
      )}
    </span>
  );
}

/**
 * "Everyone · per-member" filter row from the app chrome spec (DESIGN-BRIEF §3, screen 4).
 * Horizontally scrollable so 4–6 members never wrap.
 */
export function MemberFilterChips({
  members,
  selected,
  onSelect,
}: {
  members: readonly MemberView[];
  selected: string | null;
  onSelect: (memberId: string | null) => void;
}): JSX.Element {
  return (
    <div
      className="-mx-4 flex gap-2 overflow-x-auto px-4 py-2"
      data-testid="member-filter"
      role="group"
      aria-label="Filter by member"
    >
      <button
        type="button"
        onClick={() => onSelect(null)}
        data-testid="member-filter-all"
        aria-pressed={selected === null}
        className={`flex h-11 shrink-0 items-center rounded-full px-4 text-sm font-semibold transition-colors duration-150 ease-app ${
          selected === null
            ? 'bg-inverse text-inverse-fg'
            : 'border border-line bg-surface text-fg-2'
        }`}
      >
        Everyone
      </button>
      {members.map((member) => {
        const active = selected === member.id;
        return (
          <button
            key={member.id}
            type="button"
            onClick={() => onSelect(active ? null : member.id)}
            data-testid={`member-filter-${member.id}`}
            aria-pressed={active}
            title={member.name}
            style={active ? { ...member.style.tint, ...member.style.text } : undefined}
            className={`flex h-11 shrink-0 items-center gap-2 rounded-full pl-1 pr-3 text-sm font-semibold transition-colors duration-150 ease-app ${
              active ? '' : 'border border-line bg-surface text-fg-2'
            }`}
          >
            <MemberAvatar member={member} size="lg" />
            <span className={active ? '' : 'sr-only sm:not-sr-only'}>{member.name}</span>
          </button>
        );
      })}
    </div>
  );
}
